import React, { useEffect, useState } from "react";
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Collapse,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search as SearchIcon,
  Euro as EuroIcon,
  Timeline as TimelineIcon,
  Build as BuildIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';
import { base_url } from "../../config/config";
import axios from 'axios';

// Stat card component
const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%', bgcolor: color ? `${color}.50` : 'background.paper' }}>
    <CardContent>
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          {icon}
        </Grid>
        <Grid item xs>
          <Typography variant="h5" component="div" fontWeight="bold">
            {value}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

// Row component for expandable table
const Row = ({ anr, data, invoiceData }) => {
  const [open, setOpen] = useState(false);
  
  const { 
    totalTime = 0, 
    laborCost = 0, 
    materialCosts = 0, 
    salesAmount = 0 
  } = data;
  
  const profitLoss = salesAmount - (laborCost + materialCosts);
  const isProfitable = profitLoss > 0;
  
  const workHours = Array.isArray(data.workHours) ? data.workHours : [];
  const materials = Array.isArray(data.materials) ? data.materials : [];
  const invoices = Array.isArray(invoiceData) ? invoiceData : [];

  return (
    <>
      <TableRow 
        sx={{ 
          '& > *': { borderBottom: 'unset' },
          backgroundColor: isProfitable ? '#e8f5e9' : '#ffebee'
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <Typography variant="body1" fontWeight="medium">{anr}</Typography>
        </TableCell>
        <TableCell align="right">{totalTime.toFixed(2)} hrs</TableCell>
        <TableCell align="right">{laborCost.toFixed(2)} €</TableCell>
        <TableCell align="right">{materialCosts.toFixed(2)} €</TableCell>
        <TableCell align="right">{salesAmount.toFixed(2)} €</TableCell>
        <TableCell align="right">
          <Typography 
            variant="body1" 
            fontWeight="bold" 
            color={isProfitable ? 'success.main' : 'error.main'}
          >
            {profitLoss.toFixed(2)} €
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Chip 
            label={isProfitable ? 'Profit' : 'Loss'} 
            color={isProfitable ? 'success' : 'error'} 
            size="small" 
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Work Hours
              </Typography>
              {workHours.length > 0 ? (
                <Table size="small" aria-label="work hours">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Employee</TableCell>
                      <TableCell>Start Time</TableCell>
                      <TableCell>End Time</TableCell>
                      <TableCell>Total Time</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {workHours.map((hour, index) => (
                      <TableRow key={index}>
                        <TableCell>{hour.date}</TableCell>
                        <TableCell>{hour.user}</TableCell>
                        <TableCell>{hour.startTime}</TableCell>
                        <TableCell>{hour.endTime}</TableCell>
                        <TableCell>{hour.totalTime} hrs</TableCell>
                        <TableCell>{hour.description}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">No work hours found</Typography>
              )}
              
              <Typography variant="h6" gutterBottom component="div" sx={{ mt: 3 }}>
                Material Costs
              </Typography>
              {materials.length > 0 ? (
                <Table size="small" aria-label="materials">
                  <TableHead>
                    <TableRow>
                      <TableCell>Material</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Article Number</TableCell>
                      <TableCell>Units</TableCell>
                      <TableCell>Unit Cost</TableCell>
                      <TableCell>Total Cost</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {materials.map((material, index) => (
                      <TableRow key={index}>
                        <TableCell>{material.name}</TableCell>
                        <TableCell>{material.companyName}</TableCell>
                        <TableCell>{material.articleNumber}</TableCell>
                        <TableCell>{material.units}</TableCell>
                        <TableCell>{material.unitCost} €</TableCell>
                        <TableCell>{material.totalCost} €</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">No materials found</Typography>
              )}
              
              <Typography variant="h6" gutterBottom component="div" sx={{ mt: 3 }}>
                Invoices
              </Typography>
              {invoices.length > 0 ? (
                <Table size="small" aria-label="invoices">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice Number</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoices.map((invoice, index) => (
                      <TableRow key={index}>
                        <TableCell>{invoice.rechnungsNummer}</TableCell>
                        <TableCell>{invoice.rechnungsDatum}</TableCell>
                        <TableCell>{invoice.firmenname}</TableCell>
                        <TableCell>{invoice.gesamtbetrag} €</TableCell>
                        <TableCell>
                          <Chip 
                            size="small" 
                            label={invoice.bezahltAm ? "Paid" : "Unpaid"} 
                            color={invoice.bezahltAm ? "success" : "default"} 
                            variant={invoice.bezahltAm ? "filled" : "outlined"} 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <Typography color="text.secondary">No invoices found</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const ProfitLossInvoices = () => {
  const [anrData, setAnrData] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredAnrs, setFilteredAnrs] = useState([]);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({
    totalProjects: 0,
    profitable: 0,
    loss: 0,
    totalRevenue: 0,
    totalCosts: 0,
    totalProfit: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get all ANRs with their working hours
        const projectUsersResponse = await axios.get(`${base_url}/v1/getAllUserProjects`);
        
        // Get all projects with material costs
        const projectsResponse = await axios.get(`${base_url}/v1/getAllProjects`);
        
        // Get all invoices
        const invoicesResponse = await axios.get(`${base_url}/v1/getInvoices`);
        
        if (projectUsersResponse.data && projectsResponse.data && invoicesResponse.data) {
          // Process the data
          const workHours = projectUsersResponse.data.data || [];
          const projects = projectsResponse.data.data || [];
          const invoiceData = invoicesResponse.data.data || [];
          
          // Map invoices by Zuordnung (ANR)
          const mappedInvoices = {};
          invoiceData.forEach(invoice => {
            // Map all Zuordnung fields that contain ANR numbers
            ['zuordnung1', 'zuordnung2', 'zuordnung3', 'zuordnung4', 'zuordnung5', 'zuordnung6'].forEach(field => {
              if (invoice[field]) {
                const anr = invoice[field];
                if (!mappedInvoices[anr]) {
                  mappedInvoices[anr] = [];
                }
                mappedInvoices[anr].push(invoice);
              }
            });
          });
          
          // Group work hours by ANR
          const anrWorkHours = {};
          workHours.forEach(record => {
            if (record.ANR) {
              if (!anrWorkHours[record.ANR]) {
                anrWorkHours[record.ANR] = [];
              }
              
              // Calculate hours from start and end time
              let totalTime = 0;
              if (record.StartTime && record.EndTime) {
                const startParts = record.StartTime.split(':');
                const endParts = record.EndTime.split(':');
                
                if (startParts.length >= 2 && endParts.length >= 2) {
                  const startHour = parseInt(startParts[0], 10);
                  const startMin = parseInt(startParts[1], 10);
                  const endHour = parseInt(endParts[0], 10);
                  const endMin = parseInt(endParts[1], 10);
                  
                  const startTotalMins = startHour * 60 + startMin;
                  const endTotalMins = endHour * 60 + endMin;
                  
                  // Handle if end time is on the next day
                  const diffMins = endTotalMins >= startTotalMins 
                    ? endTotalMins - startTotalMins 
                    : (24 * 60 - startTotalMins) + endTotalMins;
                  
                  totalTime = diffMins / 60; // Convert minutes to hours
                }
              } else if (record.TotalTime) {
                // If TotalTime is already provided
                totalTime = parseFloat(record.TotalTime) || 0;
              }
              
              anrWorkHours[record.ANR].push({
                date: record.date,
                user: record.user,
                description: record.description,
                startTime: record.StartTime,
                endTime: record.EndTime,
                totalTime: totalTime.toFixed(2)
              });
            }
          });
          
          // Map material costs by ANR
          const anrMaterials = {};
          projects.forEach(project => {
            if (project.ANR && project.materialCosts && project.materialCosts.length > 0) {
              anrMaterials[project.ANR] = project.materialCosts.map(material => ({
                name: material.name,
                companyName: material.companyName,
                articleNumber: material.Artikelnummer,
                units: material.units,
                unitCost: material.singleCost,
                totalCost: material.TotalMaterialCost
              }));
            }
          });
          
          // Combine all data by ANR
          const combinedData = {};
          const allAnrs = new Set([
            ...Object.keys(anrWorkHours), 
            ...Object.keys(anrMaterials),
            ...Object.keys(mappedInvoices)
          ]);
          
          allAnrs.forEach(anr => {
            // Skip empty ANRs
            if (!anr) return;
            
            const workHoursForAnr = anrWorkHours[anr] || [];
            const materialsForAnr = anrMaterials[anr] || [];
            const invoicesForAnr = mappedInvoices[anr] || [];
            
            // Find the project with matching ANR to get MengeCost
            const matchingProject = projects.find(p => p.ANR === anr);
            const mengeCost = matchingProject && matchingProject.MengeCost 
              ? parseFloat(matchingProject.MengeCost) || 0 
              : 0;
            
            // Calculate total work hours
            let totalTime = 0;
            workHoursForAnr.forEach(wh => {
              totalTime += parseFloat(wh.totalTime) || 0;
            });
            
            // Calculate labor cost (hours * MengeCost)
            const laborCost = totalTime * mengeCost;
            
            // Calculate total material cost
            let materialCosts = 0;
            materialsForAnr.forEach(mat => {
              materialCosts += parseFloat(mat.totalCost) || 0;
            });
            
            // Calculate total sales amount from invoices
            let salesAmount = 0;
            invoicesForAnr.forEach(inv => {
              salesAmount += parseFloat(inv.gesamtbetrag) || 0;
            });
            
            combinedData[anr] = {
              workHours: workHoursForAnr,
              materials: materialsForAnr,
              totalTime,
              laborCost,
              materialCosts,
              salesAmount
            };
          });
          
          setAnrData(combinedData);
          setInvoices(mappedInvoices);
          setFilteredAnrs(Object.keys(combinedData).sort());
          
          // Calculate statistics
          const statsData = {
            totalProjects: Object.keys(combinedData).length,
            profitable: 0,
            loss: 0,
            totalRevenue: 0,
            totalCosts: 0,
            totalProfit: 0
          };
          
          Object.entries(combinedData).forEach(([anr, data]) => {
            const { laborCost = 0, materialCosts = 0, salesAmount = 0 } = data;
            const totalCost = laborCost + materialCosts;
            const profit = salesAmount - totalCost;
            
            statsData.totalRevenue += salesAmount;
            statsData.totalCosts += totalCost;
            statsData.totalProfit += profit;
            
            if (profit > 0) {
              statsData.profitable++;
            } else if (profit < 0) {
              statsData.loss++;
            }
          });
          
          setStats(statsData);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Filter ANRs based on search input
  useEffect(() => {
    if (search.trim() === '') {
      setFilteredAnrs(Object.keys(anrData).sort());
    } else {
      const filtered = Object.keys(anrData).filter(anr => 
        anr.toLowerCase().includes(search.toLowerCase())
      );
      setFilteredAnrs(filtered);
    }
  }, [search, anrData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Project Profit & Loss Analysis
      </Typography>
      
      {/* Stats section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Projects"
            value={stats.totalProjects}
            icon={<TimelineIcon sx={{ fontSize: 40, color: 'primary.main' }} />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Profitable Projects"
            value={stats.profitable}
            icon={<EuroIcon sx={{ fontSize: 40, color: 'success.main' }} />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Loss-making Projects"
            value={stats.loss}
            icon={<EuroIcon sx={{ fontSize: 40, color: 'error.main' }} />}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Profit/Loss"
            value={`${stats.totalProfit.toFixed(2)} €`}
            icon={<ShoppingCartIcon sx={{ fontSize: 40, color: stats.totalProfit >= 0 ? 'success.main' : 'error.main' }} />}
            color={stats.totalProfit >= 0 ? "success" : "error"}
          />
        </Grid>
      </Grid>
      
      {/* Search box */}
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="Search by ANR..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
      />
      
      {/* Projects table */}
      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow sx={{ bgcolor: 'background.default' }}>
              <TableCell />
              <TableCell>ANR</TableCell>
              <TableCell align="right">Work Hours</TableCell>
              <TableCell align="right">Labor Cost</TableCell>
              <TableCell align="right">Material Cost</TableCell>
              <TableCell align="right">Sales Amount</TableCell>
              <TableCell align="right">Profit/Loss</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAnrs.length > 0 ? (
              filteredAnrs.map((anr) => (
                <Row 
                  key={anr} 
                  anr={anr} 
                  data={anrData[anr] || {}} 
                  invoiceData={invoices[anr] || []}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    No projects found matching your search criteria
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProfitLossInvoices;